<script setup>
import { computed, defineEmits, defineProps, reactive, ref, toRefs, watch } from "vue";
import { useStore } from "vuex";
import PriceBreakdownSection from "@/components/ExperiencePage/SideBarMenu/PriceBreakdownSection.vue";
import { isSessionTypeOnlyVolunteer } from "@/components/ExperiencePage/utils.js";
import ArrowRight from "@/components/shared/icons/ArrowRight.vue";
import SkeletonLoader from "@/components/shared/loaders/BaseSkeletonLoader.vue";
import {
  hasConfigurableRegistrationEnabled,
  showNewExcursionConfiguratorDesign,
} from "@/composables/authorization.js";
import getApplicableContract from "@/composables/useBillings";
import { INTERNSHIP_SECTION_TITLES, PERMISSIONS } from "@/constants";
import { REGISTER_TYPE } from "@/constants.js";
import { formatDate, hideSessionDeadline } from "@/mixins/helpers";
import { isSessionInPast } from "@/util/date";
import { isFacultyLed } from "@/util/isFacultyLed";
import PanelContainer from "../Filters/PanelContainer.vue";

const emit = defineEmits(["preview", "selectSession", "applyClicked", "setMenuItems"]);
const OPEN_TO_ALL_LEVELS_ID = "e242f827-8564-45dc-94e9-abfbe91e0aae";

const props = defineProps({
  sessionId: {
    type: String,
    required: true,
  },
  sessions: {
    type: Array,
    required: true,
  },
  isLoading: {
    type: Boolean,
    default: true,
  },
  showAbout: {
    type: Boolean,
    default: false,
  },
  programPage: {
    type: Object,
    default() {
      return {};
    },
  },
  registerType: {
    type: String,
    default: "",
  },
});

const scrollTarget = ref("");

const { sessionId, sessions } = toRefs(props);

const getSelectedSession = () => sessions.value?.find((session) => session?.id === sessionId.value) || {};

const selectedSession = ref(getSelectedSession());

const store = useStore();

const billingRules = computed(() => store.state.billingRules);
const currentUser = computed(() => store.state.currentUser);
const isParticipant = computed(() => store.getters.getPermissions.includes(PERMISSIONS.PARTICIPANTS));

const showPrice = computed(() => {
  if (isSessionInPast(selectedSession.value)) return false;

  const contracts = billingRules.value?.agreements ?? [];
  const applicableContract = getApplicableContract(contracts, selectedSession.value);

  return (
    !currentUser.value ||
    contracts.length === 0 ||
    applicableContract?.direct_billing_type === "Pass through" ||
    !isParticipant.value
  );
});

watch([sessionId, sessions], () => {
  const session = getSelectedSession();
  if (session?.id) {
    selectedSession.value = session;
    setMenuVisibility();
  }
});

const displayedProficiencyLevel = computed(() => {
  const proficiency = selectedSession?.value?.min_proficiency_level?.value;
  if (!proficiency || proficiency === "No Background") {
    return "";
  }
  return proficiency.toLowerCase();
});

const getLanguages = computed(() => {
  const languages = selectedSession?.value?.required_languages;
  if (!languages || languages.length === 0) {
    return "";
  }
  return languages.map((lang, index) => lang.value + (index < languages.length - 1 ? ", " : "")).join("");
});
const getEducationLevels = computed(() => {
  const { education_levels } = selectedSession.value;
  return education_levels
    .map((educationLevel, index) => `${educationLevel.value} ${index !== education_levels.length - 1 ? "or" : ""}`)
    .join(" ");
});

const getMinimumAcademicLevel = computed(() => {
  return selectedSession.value?.academic_level?.academic_year ?? "";
});

const getMinimumAge = computed(() => {
  return Math.abs(selectedSession.value.minimum_age) ?? "";
});

const getMaximumAge = computed(() => {
  return Math.abs(selectedSession.value.maximum_age) ?? "";
});

const activeHousings = computed(() => {
  return (
    selectedSession.value?.session_units
      ?.map((session_units) => ({
        ...session_units.unit?.housings,
      }))
      ?.filter((item) => {
        return item?.status === "Active";
      }) ?? []
  );
});

const panelTitle = computed(() => {
  if (props.registerType === REGISTER_TYPE.PERSONALIZE || props.registerType === REGISTER_TYPE.REGISTER)
    return "Registration Details";
  else return "Application Details";
});

const menuItems = reactive({
  about: {
    index: 1,
    links: "about",
    sidebarlink: "sidebar-about",
    title: "About",
    altText: "About section icon",
    icon: "/images/about-icon.svg",
    visible: false,
  },
  dates: {
    index: 2,
    links: "dates",
    sidebarlink: "sidebar-dates",
    title: "Program dates",
    altText: "Program dates section icon",
    icon: "/images/program-dates-icon.svg",
    visible: false,
  },
  location: {
    index: 3,
    links: "location",
    sidebarlink: "sidebar-location",
    title: "Location",
    altText: "Location section icon",
    icon: "/images/location-icon.svg",
    visible: false,
  },
  host: {
    index: 4,
    links: "host",
    sidebarlink: "sidebar-host",
    title: "Host Institution",
    altText: "Host institution section icon",
    icon: "/images/institution-icon.svg",
    visible: false,
  },
  details: {
    index: 5,
    links: "academicdetails",
    title: "Academics",
    sidebarlink: "sidebar-academicdetails",
    altText: "Academics section icon",
    icon: "/images/details-icon.svg",
    visible: false,
  },
  housing: {
    index: 6,
    links: "housing",
    sidebarlink: "sidebar-housing",
    title: "Housing",
    altText: "Housing section icon",
    icon: "/images/housing-icon.svg",
    visible: false,
  },
  internships: {
    index: 7,
    links: "internships",
    sidebarlink: "sidebar-internships",
    title: "Internships",
    altText: "Internships section icon",
    icon: "/images/internship-icon.svg",
    visible: false,
  },
  excursions: {
    index: 8,
    links: "excursions",
    sidebarlink: "sidebar-excursions",
    title: "Excursions & Events",
    altText: "Excursions and events section icon",
    icon: "/images/excursion-icon.svg",
    visible: false,
  },
  scholarship: {
    index: 9,
    links: "scholarship",
    sidebarlink: "sidebar-scholarship",
    title: "Scholarships",
    altText: "Scholarships section icon",
    icon: "/images/scholarship-icon.svg",
    visible: false,
  },
});

const activeMenuItems = computed(() => {
  const activeItems = [];
  for (const item in menuItems) {
    if (menuItems[item]["visible"] === true) activeItems.push(menuItems[item]);
  }
  return activeItems;
});

watch(
  () => activeMenuItems.value,
  (newMenu) => {
    emit("setMenuItems", newMenu);
  },
);

watch(
  () => props.showAbout,
  () => {
    setMenuVisibility();
  },
);

const hasAcademicDescription = computed(() => {
  const academicDescription = selectedSession?.value?.academic_description?.content;
  return academicDescription && Object.values(academicDescription)[0]?.content;
});

const scrollToSection = (id) => {
  scrollTarget.value = document.getElementById(id);
  if (scrollTarget.value) {
    scrollTarget.value.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  }
};

const formatGPA = (gpa) => {
  const parsedGPA = Number.parseFloat(gpa);
  if (isNaN(parsedGPA)) return null;
  return parsedGPA % 1 !== 0 ? parsedGPA.toFixed(2) : parsedGPA.toFixed(1);
};

const setMenuVisibility = () => {
  if (!selectedSession.value?.id) {
    return;
  }
  for (const menu of Object.keys(menuItems)) {
    menuItems[menu].visible = false;
    switch (menuItems[menu].links) {
      case "internships":
        menuItems[menu].visible = Boolean(selectedSession.value.internship_filter_criteria);
        if (isSessionTypeOnlyVolunteer(selectedSession?.value)) {
          menuItems["internships"]["title"] = INTERNSHIP_SECTION_TITLES.placements;
        }
        break;

      case "location":
        if (selectedSession.value.session_travel_details?.[0]?.arrival_city?.reason_to_experience) {
          menuItems[menu].visible = true;
        }
        break;
      case "housing":
        if (activeHousings.value?.length > 0) {
          menuItems[menu].visible = true;
        }
        break;
      case "academicdetails":
        if (selectedSession.value?.class_filter_criteria || hasAcademicDescription.value) {
          menuItems[menu].visible = true;
        }
        break;
      case "excursions":
        if (selectedSession.value?.session_events?.length > 0) {
          menuItems[menu].visible = true;
        }
        // AP-5593: rename behind the show-new-excursion-configurator-design flag
        menuItems[menu].title = showNewExcursionConfiguratorDesign.value
          ? "Curated Experiences"
          : "Excursions & Events";
        break;
      case "scholarship":
        menuItems[menu].visible = props.programPage?.display_scholarship_opportunities;
        break;
      case "host":
        if (
          selectedSession.value?.session_hosting_institutions?.length > 0 &&
          !isFacultyLed(selectedSession.value?.session_types)
        ) {
          menuItems[menu].visible = true;
        }
        break;
      case "about":
        if (props.showAbout) {
          menuItems[menu].visible = true;
        }
        break;
      default:
        menuItems[menu].visible = true;
        break;
    }
  }
};

const gpaExceptionRequirements = computed(() => {
  const items = selectedSession.value?.gpa_exception_requirements || [];
  return items.map((item) => item.name).join(", ");
});

const showEducationAgeEligibilitySection = computed(() => {
  return (
    getMinimumAcademicLevel.value ||
    selectedSession.value?.education_levels?.length > 0 ||
    getMinimumAge.value ||
    getMaximumAge.value
  );
});

const showAdmissionsSection = computed(() => {
  return selectedSession.value?.prerequisites || selectedSession.value?.corequisities;
});

const hasRequirements = computed(
  () =>
    !!(
      selectedSession.value?.education_level_minimum_age_rule ||
      selectedSession.value?.minimum_gpa_requirement ||
      selectedSession.value?.min_proficiency_level_id ||
      selectedSession.value?.required_languages?.length
    ),
);

const sidebarCredits = computed(() => {
  const max = selectedSession.value?.academic_max_credits ?? 0;
  const min = selectedSession.value?.academic_min_credits ?? 0;
  if (max === 0 && min === 0) {
    return undefined;
  }

  if (max === min) {
    return String(max);
  }

  if (max > min && min !== 0) {
    return `${min}-${max}`;
  }

  return String(max || min);
});

const isOpenToAllLevels = computed(() => {
  return selectedSession.value?.min_proficiency_level_id === OPEN_TO_ALL_LEVELS_ID;
});
</script>

<template>
  <div class="px-4 md:px-10">
    <div>
      <div class="mb-4">
        <SkeletonLoader
          v-if="isLoading"
          :from-color="'#E3E7F3'"
          :to-color="'#F9FAFB'"
          :height="'h-13'"
          class="mb-4"
          rounded
        />
        <SkeletonLoader v-if="isLoading" :from-color="'#E3E7F3'" :to-color="'#F9FAFB'" :height="'h-16'" rounded />
      </div>
      <PanelContainer id="city-filter" :title="panelTitle">
        <div class="mb-4 mt-4">
          <SkeletonLoader v-if="isLoading" from-color="#E3E7F3" to-color="#F9FAFB" height="h-20" rounded />
          <div v-else>
            <p v-if="hasRequirements" class="font-bold text-indigo-base text-base">Requirements:</p>
            <div v-if="hasConfigurableRegistrationEnabled" class="text-blue-750 text-sm">
              <div v-if="selectedSession.is_gpa_required">
                <h3 class="font-bold">GPA</h3>
                <ul class="pl-6 list-disc">
                  <li v-if="selectedSession.minimum_gpa_requirement">
                    Minimum GPA:
                    {{ formatGPA(selectedSession.minimum_gpa_requirement) }}
                  </li>
                  <li>
                    Minimum GPA Exceptions Allowed: {{ selectedSession.gpa_exceptions_allowed ? "Yes" : "No" }}
                  </li>
                  <li v-if="selectedSession.gpa_exception_requirements?.length > 0">
                    Minimum GPA Exception Requirements: {{gpaExceptionRequirements}}
                  </li>
                </ul>
              </div>
              <div v-if="showEducationAgeEligibilitySection">
                <h3 class="font-bold">Education & Age Eligibility</h3>
                <ul class="pl-6 list-disc">
                  <li v-if="getMinimumAcademicLevel">
                    Minimum Year in School: {{ getMinimumAcademicLevel }}
                  </li>
                  <li v-if="selectedSession.education_levels?.length > 0">
                    Open to: {{ getEducationLevels }}
                  </li>
                  <li v-if="getMinimumAge">Minimum age: {{ getMinimumAge }}</li>
                  <li v-if="getMaximumAge">Maximum age: {{ getMaximumAge }}</li>
                </ul>
              </div>
              <div v-if="showAdmissionsSection">
                <span class="font-bold">Admissions</span>
                <ul class="pl-6 list-disc">
                  <li v-if="selectedSession.prerequisites">
                    Required for Acceptance: {{ selectedSession.prerequisites }}
                  </li>
                  <li v-if="selectedSession.corequisities">
                    Co-requisites: {{ selectedSession.corequisities }}
                  </li>
                </ul>
              </div>
              <div v-if="selectedSession.required_languages?.length > 0">
                <h3 class="font-bold">Language</h3>
                <ul class="pl-6 list-disc">
                  <template v-if="isOpenToAllLevels">
                    <li v-for="lang in selectedSession.required_languages" :key="lang.id">
                      Language Level: all levels {{ lang.value }} speakers
                    </li>
                  </template>
                  <li v-else>
                    Language Level: {{ displayedProficiencyLevel }} {{ getLanguages }} speakers
                  </li>
                </ul>
              </div>
            </div>
            <div v-else>
              <li v-if="getMinimumAcademicLevel" class="text-indigo-base">
                Min Year in School: {{ getMinimumAcademicLevel }}
              </li>
              <li v-if="selectedSession.minimum_gpa_requirement" class="text-indigo-base">
                Minimum
                {{ formatGPA(selectedSession.minimum_gpa_requirement) }}
                G.P.A.
              </li>
              <li v-if="getMinimumAge" class="text-indigo-base">Minimum age {{ getMinimumAge }}</li>
              <li v-if="getMaximumAge" class="text-indigo-base">Maximum age {{ getMaximumAge }}</li>
              <li v-if="selectedSession.education_levels?.length > 0" class="text-indigo-base education-levels">
                Open to {{ getEducationLevels }}
              </li>
              <template v-if="selectedSession.required_languages?.length > 0">
                <template v-if="isOpenToAllLevels">
                  <li
                    v-for="lang in selectedSession.required_languages"
                    :key="lang.id"
                    class="text-indigo-base"
                  >
                     Language Level: all levels {{ lang.value }} speakers
                  </li>
                </template>
                <li v-else class="text-indigo-base">
                  Open to {{ displayedProficiencyLevel }} {{ getLanguages }} speakers
                </li>
              </template>
              <li v-if="selectedSession.prerequisites" class="text-indigo-base">
                Required for Acceptance: {{ selectedSession.prerequisites }}
              </li>
              <li v-if="selectedSession.corequisities" class="text-indigo-base">
                Corequisites: {{ selectedSession.corequisities }}
              </li>
            </div>
          </div>
        </div>
        <SkeletonLoader v-if="isLoading" from-color="#E3E7F3" to-color="#F9FAFB" height="h-10" rounded />
        <div v-else-if="!hideSessionDeadline(selectedSession)" class="mb-4">
          <p class="font-bold text-indigo-base text-base">
            Next session deadline:
            {{ formatDate(selectedSession.application_deadline, "long") }}
          </p>
        </div>
        <SkeletonLoader v-if="isLoading" :from-color="'#E3E7F3'" :to-color="'#F9FAFB'" :height="'h-10'" rounded />
        <div v-else-if="selectedSession.base_price_in_cents && props.programPage?.display_starting_price" class="mb-4">
          <PriceBreakdownSection
            v-if="showPrice"
            :selected-session="selectedSession"
            @preview="$emit('preview', true)"
          />
        </div>
        <SkeletonLoader v-if="isLoading" :from-color="'#E3E7F3'" :to-color="'#F9FAFB'" :height="'h-10'" rounded />
        <div v-else class="mb-4">
          <p class="font-montserrat font-bold text-indigo-base text-15p">
            Visa Required for US Students:
            {{ !!selectedSession.visa_required ? "Yes" : "No" }}
          </p>
          <p class="font-montserrat font-medium text-sm text-indigo-base mt-1">
            *Non-US Citizens should check visa requirements via the appropriate consulate.
          </p>
          <p v-if="!!selectedSession.visa_required" class="font-montserrat font-medium text-sm text-indigo-base">
            Learn more about visas
            <a
              href="https://apiabroad.com/student-visas/"
              target="_blank"
              class="font-semibold underline"
              :class="[secondaryColor ? `${secondaryColorClass}` : 'text-university-secondary']"
              >here.</a
            >
          </p>
        </div>
        <SkeletonLoader v-if="isLoading" :from-color="'#E3E7F3'" :to-color="'#F9FAFB'" :height="'h-10'" rounded />
        <div v-if="sidebarCredits && !isLoading" class="mb-4">
          <p class="font-bold text-indigo-base text-base">Credits: {{ sidebarCredits }} US Semester Credits</p>
        </div>
        <SkeletonLoader v-if="isLoading" :from-color="'#E3E7F3'" :to-color="'#F9FAFB'" :height="'h-10'" rounded />
        <div v-if="selectedSession?.transcript_provider?.name && !isLoading" class="mb-4">
          <p class="font-bold text-indigo-base text-base">Transcript:</p>
          <p class="text-sm text-indigo-base">
            Participants will receive a transcript from
            {{ selectedSession?.transcript_provider?.name }}
          </p>
        </div>
      </PanelContainer>
    </div>
  </div>
  <SkeletonLoader
    v-if="!props.sessionId || isLoading"
    :from-color="'#D5E9E7'"
    :to-color="'#F9FAFB'"
    :height="'h-14'"
    class="mb-4"
    rounded
  />

  <Teleport v-if="props.sessionId" to="#sticky-sidebar-items">
    <template v-if="isLoading && !sessions.length">
      <div
        v-for="(_, key) in menuItems"
        :key="key"
        class="px-4 md:pr-[50px] md:pl-[30px] mb-1"
      >
        <SkeletonLoader from-color="#E3E7F3" to-color="#F9FAFB" height="h-14" rounded />
      </div>
    </template>
    <template v-else>
      <div
        v-for="menu in menuItems"
        :id="menu.sidebarlink"
        :key="menu.index"
        class="menu-items px-4 md:pr-[50px] md:pl-[30px] text-indigo-base font-montserrat"
      >
        <div v-if="menu.visible">
          <div class="border-b border-indigo-base/20 cursor-pointer" @click="scrollToSection(menu.links)">
            <div class="grid grid-cols-sidebar-experience-item pt-2.5 pb-2.5 gap-x-2.5 items-center">
              <img :src="menu.icon" loading="lazy" :alt="menu.altText" />
              <span class="font-bold capitalize text-lg tracking-tigh">
                {{ menu.title }}
              </span>
              <ArrowRight custom-class="w-5 h-5 col-start-3 col-span-1 items-center" />
            </div>
          </div>
        </div>
      </div>
    </template>
  </Teleport>
</template>

<style scoped>
.session-select-component-style :deep(.vs__dropdown-toggle) {
  overflow: auto;
  max-height: fit-content;
  display: flex;
  border-color: white;
  padding-left: 0.1rem;
  padding-right: 0.1rem;
}
.session-select-component-style :deep(.vs__selected-options) {
  display: contents;
  overflow: hidden;
}
.session-select-component-style :deep(.vs__dropdown-menu) .vs__dropdown-toggle {
  border-radius: 0;
  display: block;
  overflow: hidden;
  word-wrap: break-word;
}
.session-select-component-style :deep(.vs__dropdown-option) {
  white-space: normal;
}
.session-select-component-style :deep(.vs__dropdown-option--highlight) {
  background: #edf9f8 !important;
  cursor: auto;
}
.session-select-component-style :deep(.vs__selected) {
  display: block;
  width: 100%;
  position: relative !important;
}
</style>
